import { entityDefinitions, isCollectible, ROOF_HEIGHT } from "../data/entities";
import type { BoardPower, InputAction, Lane, PowerKind, StumbleType, WorldEntity } from "../data/model";
import type { ChunkDefinition, RouteDefinition } from "../data/chunkTypes";
import { flight as F, motor as M, powers as P, revive as RV, score as SC, stumble as ST, world as W } from "../data/tuning";
import { CAR_HALF_LENGTH, classify, entityTop, overlapX, overlapZ, supportUnder, surfaceTop, type CarBox } from "./Collision";
import { Motor, type MotorEvents, type Support } from "./Motor";
import { PowerSystem, type PowerLevels } from "./Powers";
import { Chaser, StumbleBehaviour } from "./Stumble";
import { World, type FlightKind, type Landing, type SpawnContext } from "./World";

export type CueName = "coin" | "jump" | "duck" | "lane" | "power" | "shield" | "crash" | "stumble" | "siren" | "quest" | "land" | "nitro" | "revive" | "key" | "board" | "box" | "high" | "ui" | "fly" | "bump";

export interface RunHooks {
  cue(name: CueName): void;
  toast(message: string): void;
  gameOver(caught: boolean): void;
  shake(): void;
}

export interface RunOptions {
  random?: () => number;
  tutorial?: boolean;
  /** the original starts every run inside a 5 s stumble window (the cop right behind); default on outside the tutorial */
  initialStumble?: boolean;
  levels?: PowerLevels;
  boards?: number;
  boardPowers?: BoardPower[];
  /** the powers of every board the mystery power-up may hand out (the original: any board in the store); default: your own board */
  randomBoards?: BoardPower[][];
  /** pre-run boosters */
  headstart?: 0 | 1 | 2 | 3;
  booster?: boolean;
  shield?: boolean;
  /** permanent multiplier from completed mission sets (x1 … x30) */
  missionMultiplier?: number;
  letters?: string[];
  seasonTokens?: boolean;
  chunks?: Record<string, ChunkDefinition>;
  route?: RouteDefinition;
  intro?: boolean;
  /** the car waits at the start until start() (the original's EnterRun: tap to start) */
  waitForTap?: boolean;
}

export type TutorialPrompt = "jump" | "roll" | "lane";

export interface RunSnapshot {
  score: number;
  coins: number;
  keys: number;
  distance: number;
  multiplier: number;
  shields: number;
  boards: number;
  boardLeft: number;
  /** the full time of the board under you (30 s yours, 15 s a mystery one), for the HUD ring */
  boardTotal: number;
  lane: Lane;
  playerX: number;
  playerY: number;
  playerZ: number;
  vy: number;
  ducking: boolean;
  airborne: boolean;
  flying: boolean;
  nitro: boolean;
  protected: boolean;
  speed: number;
  supportHeight: number;
  chaser: number;
  chaserDistance: number;
  chaserX: number;
  chaserY: number;
  hunted: boolean;
  caught: boolean;
  state: string;
  powerups: Record<string, number>;
  idle: boolean;
  boosting: boolean;
  tutorialPrompt: TutorialPrompt | null;
  tutorialDone: boolean;
  timeScale: number;
}

export interface RunMetrics {
  coins: number; coinsWithMagnet: number; coinsInAir: number; coinsWithSneakers: number; coinsWithJetpack: number;
  jumps: number; rolls: number; rides: number; dodges: number; stumbles: number; stumblesByKind: Partial<Record<string, number>>;
  escapes: number; pickups: Partial<Record<string, number>>; keys: number; boxes: number; letters: string[]; tokens: number;
  boardsUsed: number; revives: number; score: number; distance: number; magnets: number; hops: number; ducks: number;
  rollsMiddle: number; longestLaneSeconds: number; caughtAtSeconds: number | null; ethHopped: number; headstarts: number; boosters: number;
  pickupsByLane: Partial<Record<string, number>>;
}

/** The run: the car, the road, the rules, the cop, the score. No rendering — the scene reads snapshots and entities. */
export class RunSession {
  readonly motor = new Motor();
  readonly world: World;
  readonly stumble = new StumbleBehaviour();
  readonly chaser = new Chaser();
  readonly powers: PowerSystem;
  readonly metrics: RunMetrics = {
    coins: 0, coinsWithMagnet: 0, coinsInAir: 0, coinsWithSneakers: 0, coinsWithJetpack: 0, jumps: 0, rolls: 0, rides: 0, dodges: 0,
    stumbles: 0, stumblesByKind: {}, escapes: 0, pickups: {}, keys: 0, boxes: 0, letters: [], tokens: 0, boardsUsed: 0, revives: 0,
    score: 0, distance: 0, magnets: 0, hops: 0, ducks: 0, rollsMiddle: 0, longestLaneSeconds: 0, caughtAtSeconds: null, ethHopped: 0,
    headstarts: 0, boosters: 0, pickupsByLane: {}
  };
  private laneSeconds = 0;
  readonly startedAt = Date.now();
  score = 0;
  coins = 0;
  keys = 0;
  distance = 0;
  multiplier = 1;
  boards: number;
  ended = false;
  caught = false;
  revives = 0;
  /** what ended the run last: kind, lane, impact, the car's height and state */
  lastCrash: { kind: string; lane: number; z: number; carY: number; state: string; how: string; src?: string } | null = null;
  /** the last stumbles (dev: the death audit reads them) */
  stumbleLog: { kind: string; where: string; z: number; carY: number; state: string; src?: string; carX?: number; lane?: number; eLane?: number; eZ?: number; eTop?: number; vy?: number }[] = [];
  private elapsed = 0;
  private scoreAcc = 0;
  private mysteryBonus = 0;
  private missionMultiplier: number;
  private touching = new Set<number>();
  private lastSupport: Support["kind"] = "road";
  private pendingDeath = false;
  private readonly tutorialMode: boolean;
  private readonly initialStumble: boolean;
  private headstartLevel = 1;
  private frame = 0;
  private trippedAt = -1;
  private readonly rng: () => number;
  /** the entity hit during the current move (the original's EntityHitThisMove: side and corner hits count once per move) */
  private lastHit = { id: -1, move: -1 };
  /** a minor obstacle the car is pressed against; it no longer counts once you get away from it */
  private blockedBy = -1;
  private readonly letters: string[];
  private readonly seasonTokens: boolean;
  private readonly motorEvents: MotorEvents;
  /** the first run: time slows before the first barrier, bar and blocked lane until the right swipe comes */
  tutorialPrompt: TutorialPrompt | null = null;
  tutorialDone = false;
  timeScale = 1;
  private taught = new Set<TutorialPrompt>();
  private promptEntity: number | null = null;

  constructor(private hooks: RunHooks, options: RunOptions = {}) {
    const random = options.random ?? Math.random;
    this.rng = random;
    this.world = new World({ random, chunks: options.chunks, route: options.route, intro: options.intro, tutorial: options.tutorial });
    this.powers = new PowerSystem(options.levels, random);
    this.powers.setBoardPowers(options.boardPowers ?? []);
    this.powers.setRandomBoards(options.randomBoards ?? []);
    this.powers.onFlightEnd((kind) => this.onFlightEnd(kind));
    this.boards = options.boards ?? 0;
    this.missionMultiplier = Math.max(0, options.missionMultiplier ?? 0);
    this.tutorialMode = options.tutorial ?? false;
    this.letters = options.letters ?? [];
    this.seasonTokens = options.seasonTokens ?? false;
    this.motorEvents = {
      jump: (air) => { this.metrics.jumps += 1; this.metrics.hops += 1; this.hooks.cue(air ? "fly" : "jump"); },
      land: (hard) => { this.hooks.cue("land"); if (hard) this.hooks.shake(); },
      lane: () => { this.metrics.longestLaneSeconds = Math.max(this.metrics.longestLaneSeconds, this.laneSeconds); this.laneSeconds = 0; this.hooks.cue("lane"); },
      roll: () => { this.metrics.rolls += 1; this.metrics.ducks += 1; if (this.motor.lane === 0) this.metrics.rollsMiddle += 1; this.hooks.cue("duck"); },
      // a swipe into the wall: a bump and a shake, never a trip (the original stumbles here; Karolis: "into nothingness no")
      boundary: () => { this.hooks.cue("bump"); this.hooks.shake(); }
    };
    this.initialStumble = options.initialStumble ?? !options.tutorial;
    if (!options.tutorial) this.tutorialDone = true;
    if (!options.waitForTap) {
      this.begin();
      this.applyPreRun(options);
    }
    this.world.fill(0, this.spawnContext());
  }

  /** The pre-run boosters (headstart, score booster, shield) — applied when the run actually starts. */
  applyPreRun(options: { headstart?: 0 | 1 | 2 | 3; booster?: boolean; shield?: boolean }): void {
    if (options.shield) this.powers.shieldActive = true;
    if (options.booster) { this.powers.activate("booster", this.motor, this.powerEvents()); this.metrics.boosters += 1; }
    if (options.headstart) {
      this.metrics.headstarts += 1;
      this.tutorialDone = true;
      this.headstartLevel = Math.min(3, Math.max(1, options.headstart));
      this.powers.activate("headstart", this.motor, this.powerEvents());
      if (this.motor.flight) this.motor.flight.fixedSpeed = F.headstartSpeed[this.headstartLevel - 1];
      this.beginFlight("headstart");
    }
  }

  get entities(): WorldEntity[] {
    return this.world.entities;
  }

  get idle(): boolean {
    return this.motor.state === "idle";
  }

  /** The tap that starts the run. */
  start(): void {
    this.begin();
  }

  /** The run starts moving inside a stumble window, the cop right behind (the original's SetInitialStumble). */
  private begin(): void {
    if (!this.idle) return;
    this.motor.start();
    if (this.initialStumble) this.stumble.setInitialStumble();
  }

  /** the run speed over the start speed (the original's SpeedModifier: 1 → 2 over the ramp) */
  private speedModifier(): number {
    return Math.max(1, this.motor.rampSpeed / M.startSpeed);
  }

  get canRevive(): boolean {
    return this.ended && this.revives < RV.keyCosts.length;
  }

  get reviveCost(): number {
    return RV.keyCosts[Math.min(this.revives, RV.keyCosts.length - 1)];
  }

  update(realDt: number): void {
    if (this.ended || this.idle) return;
    this.tutorial();
    const dt = realDt * this.timeScale;
    this.elapsed += dt;
    this.frame += 1;
    this.steerFlight();
    this.powers.update(dt, this.motor);
    if (this.motor.state === "flying" && this.stumble.stumbling) this.stumble.recover();

    const before = this.motor.z;
    this.holdRollUnder();
    const car = this.carBox();
    const support = supportUnder(car, this.world.entities);
    const elevated = support.kind === "roof" || support.kind === "ramp";
    if (elevated && this.lastSupport !== "roof" && this.lastSupport !== "ramp" && !this.motor.airborne) this.metrics.rides += 1;
    this.lastSupport = support.kind;
    this.motor.update(dt, support, this.motorEvents);
    const traveled = this.motor.z - before;
    this.distance = this.motor.z;
    this.metrics.distance = this.distance;
    this.laneSeconds += dt;
    this.metrics.longestLaneSeconds = Math.max(this.metrics.longestLaneSeconds, this.laneSeconds);

    this.world.update(dt, this.motor.z, this.motor.x);
    this.world.fill(this.motor.z, this.spawnContext());
    this.collide();
    if (this.ended) return;

    const result = this.stumble.update(dt);
    if (result === "escaped") { this.metrics.escapes += 1; this.hooks.toast("LOST ELON"); }   // (the opening window closes quietly: Stumble.update)
    if (result === "death") {
      const saved = this.powers.absorbCrash();
      if (!saved) { this.finish(true); return; }
      this.pendingDeath = false;
      this.caught = false;
      this.applySave(saved);
    }
    this.chaser.update(dt, this.stumble.stumbling, this.motor.x, this.motor.y, this.motor.state === "flying", this.tutorialMode && this.distance < 300);
    this.magnet(dt);

    this.multiplier = (SC.baseMultiplier + this.missionMultiplier + this.powers.multiplierBonus() + this.mysteryBonus) * (this.powers.doubleScore ? 2 : 1);
    this.scoreAcc += Math.max(0, traveled) * SC.pointsPerMetre * this.multiplier;
    this.score = Math.floor(this.scoreAcc);
    this.metrics.score = this.score;
  }

  action(action: InputAction): void {
    if (this.ended || this.pendingDeath || this.idle) return;
    if (this.tutorialPrompt) {
      const matches = this.tutorialPrompt === "jump" ? action === "jump" : this.tutorialPrompt === "roll" ? action === "roll" : action === "left" || action === "right";
      if (!matches) return;
      this.taught.add(this.tutorialPrompt);
      this.tutorialPrompt = null;
      this.promptEntity = null;
      this.timeScale = 1;
      if (this.taught.size >= 3) this.tutorialDone = true;
    }
    if (action === "board") {
      if (this.motor.state === "flying" || this.motor.inArc) return;
      if (this.powers.useBoard(this.boards)) {
        this.stumble.recover();
        this.boards -= 1;
        this.metrics.boardsUsed += 1;
        this.hooks.cue("board");
        this.hooks.toast("HOVER BOARD");
      }
      return;
    }
    if (action === "pause") return;
    this.motor.tryAction(action, this.motorEvents);
  }

  /** True while a first-run tutorial is still teaching (a crash then continues for free — Karolis 2026-10-17). */
  get inTutorial(): boolean {
    return !this.tutorialDone;
  }

  /** A continue: back on the road, invincible for a moment, the cop pushed back, 50 m ahead cleared. `free` = the tutorial's
   *  own retry: no keys, and it does not use up one of the run's paid continues (their key costs stay in order). */
  revive(free = false): boolean {
    if (free ? !this.ended : !this.canRevive) return false;
    if (!free) {
      this.revives += 1;
      this.metrics.revives += 1;
    }
    this.ended = false;
    this.caught = false;
    this.pendingDeath = false;
    this.stumble.revive();
    this.chaser.pushBack();
    this.motor.resurrect();
    this.powers.endRun();
    this.world.clearAhead(this.motor.z, RV.cleanupDistance * this.speedModifier());
    this.touching.clear();
    this.hooks.cue("revive");
    return true;
  }

  snapshot(): RunSnapshot {
    const pose = this.chaser.pose();
    const powerups: Record<string, number> = {};
    for (const [k, p] of this.powers.active) powerups[k] = p.left;
    return {
      score: this.score, coins: this.coins, keys: this.keys, distance: Math.floor(this.distance), multiplier: this.multiplier,
      shields: this.powers.shieldActive ? 1 : 0, boards: this.boards, boardLeft: Math.max(this.powers.boardLeft, this.powers.randomBoardLeft), boardTotal: this.powers.boardLeft >= this.powers.randomBoardLeft ? P.boardSeconds : P.randomBoardSeconds,
      lane: this.motor.lane, playerX: this.motor.x, playerY: this.motor.y, playerZ: this.motor.z, vy: this.motor.vy,
      ducking: this.motor.rolled, airborne: this.motor.airborne, flying: this.motor.state === "flying", nitro: false,
      protected: this.stumble.invincible || this.powers.boardActive || this.powers.shieldActive,
      speed: this.motor.speed, supportHeight: this.motor.y, chaser: pose.closeness, chaserDistance: pose.distance, chaserX: pose.x, chaserY: pose.y,
      hunted: this.stumble.stumbling, caught: this.caught, state: this.motor.state, powerups,
      idle: this.idle, boosting: this.motor.boosting, tutorialPrompt: this.tutorialPrompt, tutorialDone: this.tutorialDone, timeScale: this.timeScale
    };
  }

  durationMs(): number {
    return Math.round(this.elapsed * 1000);
  }

  // ------------------------------------------------------------------------------------------------- internals

  /** The forced tutorial: before the first low barrier, the first bar and the first blocked lane, time crawls until the right input. */
  private tutorial(): void {
    if (this.tutorialDone) return;
    if (this.distance > 600) { this.tutorialDone = true; this.timeScale = 1; this.tutorialPrompt = null; return; }
    if (this.tutorialPrompt) {
      const e = this.world.entities.find((x) => x.id === this.promptEntity);
      if (!e || !e.active || e.z < this.motor.z + 1.5) { this.tutorialPrompt = null; this.timeScale = 1; }
      return;
    }
    const lane = this.motor.lane;
    const onRoof = this.motor.y > 3;
    let nearest: WorldEntity | undefined;
    for (const e of this.world.entities) {
      if (!e.active || isCollectible(e.kind) || e.kind === "gate" || e.kind === "ramp" || e.kind === "deck") continue;
      const def = entityDefinitions[e.kind];
      if (def.surface === "boost" || def.surface === "slow") continue;            // speed pads are driven over
      if (e.yBase >= this.motor.y + M.colliderHeight) continue;                   // overhead: driven under
      if (onRoof && e.kind === "truck") continue;
      if (Math.round(e.lane) !== lane) continue;
      const d = e.z - this.motor.z;
      if (d < 4 || d > 24) continue;
      if (!nearest || e.z < nearest.z) nearest = e;
    }
    if (!nearest) return;
    const kind: TutorialPrompt = nearest.kind === "barrierLow" || nearest.kind === "ethCoin" ? "jump"
      : nearest.kind === "barrierBar" || nearest.kind === "barrierHigh" ? "roll" : "lane";
    if (this.taught.has(kind)) return;
    // ask when acting at once works: a hop peaks ~10 m on, a duck lasts ~13 m, a lane change needs ~6 m
    const askAt = kind === "jump" ? 10 : kind === "roll" ? 8 : 14;
    if (nearest.z - this.motor.z > askAt) return;
    this.tutorialPrompt = kind;
    this.promptEntity = nearest.id;
    this.timeScale = 0.12;
  }

  private carBox(): CarBox {
    return { x: this.motor.x, z: this.motor.z, y: this.motor.y, height: this.motor.colliderHeight, halfWidth: M.colliderHalfWidth, halfLength: CAR_HALF_LENGTH, vy: this.motor.vy };
  }

  private spawnContext(): SpawnContext {
    const flying = this.motor.state === "flying";
    const landingZ = flying ? this.world.landing?.start ?? this.motor.z + this.powers.flightLeft * this.motor.speed : undefined;
    return { speed: this.motor.speed, flying, landingZ, letters: this.letters, seasonTokens: this.seasonTokens };
  }

  /**
   * A flight starts (thrusters picked up, a headstart): the World puts the landing section where it would end, at this
   * flight's fixed speed (the original: z + duration × flightSpeed), and the thrusters lay their SOL trail towards it.
   */
  private beginFlight(kind: FlightKind): void {
    const seconds = kind === "jetpack" ? this.powers.seconds("jetpack") : F.headstartSeconds;
    const flightSpeed = this.motor.flight?.fixedSpeed ?? this.motor.rampSpeed * F.jetpackSpeedMultiplier;
    this.world.scheduleLanding(kind, this.motor.z + seconds * flightSpeed, this.landingSpeed(kind, seconds));
    this.flightLanding = null;
    if (kind === "jetpack") this.world.spawnJetpackCoins(this.motor.z, seconds * flightSpeed, F.jetpackHeight, flightSpeed);
  }

  /**
   * The speed the car will land at (it sizes the open road after the touchdown): the ramp goes on in the air, a flight
   * stretched to its landing comes down a little later (4 s of margin), a headstart lands further up the ramp, and a speed
   * board adds its bit.
   */
  private landingSpeed(kind: FlightKind, seconds: number): number {
    const offset = kind === "headstart" ? F.headstartLandSpeedOffset[this.headstartLevel - 1] * M.speedRampSeconds : 0;
    const at = this.motor.elapsed + seconds + 4 + offset;
    const ramp = M.startSpeed + (M.endSpeed - M.startSpeed) * Math.min(1, at / M.speedRampSeconds);
    const board = this.motor.abilities.speedAddition;
    return ramp + (board ? Math.max(board[0], board[1]) : 0);
  }

  /** the landing the flight is steering to (to notice when the World places it) */
  private flightLanding: Landing | null = null;

  /**
   * The flight ends where its landing section starts (the original stretches the flight to land on it): the power's timer
   * shows the way left, and the flight ends when the car gets there. Until the section is placed, the timer waits for it.
   */
  private steerFlight(): void {
    if (!this.powers.flying || this.motor.state !== "flying") return;
    const landing = this.world.landing;
    if (landing) {
      const left = (landing.start - this.motor.z) / Math.max(1, this.motor.speed);
      if (left <= 0) { this.powers.endFlight(this.motor); return; }
      this.powers.setFlightLeft(left, landing !== this.flightLanding);
      this.flightLanding = landing;
    } else if (this.world.landingPending && this.powers.flightLeft < 0.1) {
      this.powers.setFlightLeft(0.1, false);
    }
  }

  private collide(): void {
    const car = this.carBox();
    if (this.blockedBy >= 0) {
      // the minor obstacle you were pressed against stops counting once you are past it or out of its lane
      const b = this.world.entities.find((x) => x.id === this.blockedBy);
      if (!b || !b.active || this.motor.z - CAR_HALF_LENGTH > b.z + b.depth || overlapX(car, b) <= 0) this.blockedBy = -1;
    }
    const flying = this.motor.state === "flying";
    const nowTouching = new Set<number>();
    for (const e of this.world.entities) {
      if (!e.active) continue;
      const dz = e.z - car.z;
      if (dz > 4 || dz + e.depth < -4) continue;
      if (isCollectible(e.kind)) {
        // a pogo stick on the road is left there while flying (the original's collider is off during the climb): it would
        // turn the flight into a hop and the flight would never come down on its landing
        if (flying && e.kind === "pickup" && e.power === "pogo") continue;
        if (overlapZ(car, e) && overlapX(car, e) > 0 && e.yBase < car.y + car.height + 0.4 && e.yBase + e.height > car.y - 0.4) this.collect(e);
        continue;
      }
      const def = entityDefinitions[e.kind];
      const zTouch = overlapZ(car, e);
      if (zTouch) {
        nowTouching.add(e.id);
        // a dodged barrier: it passes under/over you in your lane without a hit
        if (!e.counted && (e.kind === "barrierLow" || e.kind === "barrierBar" || e.kind === "barrierHigh" || e.kind === "ethCoin") && overlapX(car, e) > 0 && e.z < car.z) {
          e.counted = true;
          if (e.kind === "ethCoin") this.metrics.ethHopped += 1; else this.metrics.dodges += 1;
        }
      }
      if (flying && e.yBase + e.height < car.y - 0.5) continue;
      if (def.surface === "boost" || def.surface === "slow") {
        if (zTouch && overlapX(car, e) > 0 && car.y < e.yBase + e.height + 0.3 && !e.counted) {
          e.counted = true;
          if (def.surface === "boost") this.motor.boostUp(P.speedPadBoost[1], 2.5); else this.motor.slowDown(P.speedPadSlowFactor, 1.5);
          this.hooks.cue(def.surface === "boost" ? "nitro" : "bump");
        }
        continue;
      }
      if (e.kind === "gate") continue;
      if (def.surface === "roof" || def.surface === "ground") {
        // the surface you are on (or landing on) is not a wall; a ramp's slope can be stepped onto from the next lane while it is
        // still within the car's step-over reach (72 % of its height, the legs rule) — higher up its flank stays a side bump
        const reach = e.kind === "ramp" ? M.lowerImpactRatio * car.height : 0.6;
        if (car.y >= surfaceTop(e, car.z) - reach) continue;
      }
      const sideEntry = this.touching.has(e.id);
      // a ceiling: already under it (a bridge deck over a roof) and rising into it — the head bumps, nothing breaks
      if (sideEntry && car.vy > 0.01 && overlapX(car, e) > 0 && car.y < e.yBase && car.y + car.height > e.yBase && car.y + car.height - e.yBase < 0.6) {
        this.motor.y = Math.max(0, e.yBase - car.height);
        this.motor.vy = Math.min(0, this.motor.vy);
        continue;
      }
      if (e.id === this.blockedBy) continue;
      const impact = classify(car, e, sideEntry, this.motor.changingLane);
      if (!impact) continue;
      const type = e.stumble ?? def.stumble;                    // a falling truck is "hard"
      if (def.trigger) {
        if (!e.counted) {
          e.counted = true;
          this.hit(type, e, "frontal");
          if (e.kind === "ethCoin") { e.active = false; e.cleared = true; }   // knocked off the road
        }
        continue;
      }
      if (this.lastHit.id === e.id && this.lastHit.move === this.motor.moveId) continue;   // once per entity per move
      const { horizontal, vertical } = impact;
      const dir = this.motor.laneDirection;
      // the side of it: the car came from beside it (the original's SideDepth: front corner · middle · back corner)
      if (horizontal === "left" || horizontal === "right") {
        this.lastHit = { id: e.id, move: this.motor.moveId };
        const movingAway = dir !== 0 && (horizontal === "left" ? dir < 0 : dir > 0);
        const depth = this.sideDepth(e);
        if (depth === "front" && movingAway) { this.hit(type, e, "corner"); continue; }
        if (depth === "back") { if (!this.motor.abilities.noCornerCollision) this.hit(type, e, "corner"); continue; }
        this.motor.cancelLaneChange();
        this.hit(type, e, "side");
        continue;
      }
      // low enough to get over: set on top of it — a trip; the obstacle stays
      if (vertical === "legs") {
        this.lastHit = { id: e.id, move: this.motor.moveId };
        this.motor.landOn(entityTop(e));
        if (!this.motor.abilities.noLowerCollision) this.hit(type, e, "legs");   // Zap Sideways: no trip (NotifyLowerImpact skipped)
        continue;
      }
      // a front corner during a lane change: moving into it bounces you back (not off a hard one); either way a trip
      if (horizontal === "cornerLeft" || horizontal === "cornerRight") {
        this.lastHit = { id: e.id, move: this.motor.moveId };
        const into = (dir > 0 && horizontal === "cornerLeft") || (dir < 0 && horizontal === "cornerRight");
        if (into && type !== "hard") this.motor.bounceBack(this.motorEvents);
        else if (this.motor.abilities.noCornerCollision) continue;
        this.hit(type, e, "corner");
        continue;
      }
      // head-on (centre or head): the end of the run — unless it is a minor obstacle: a trip, pressed against it
      if (type === "minor") {
        this.blockedBy = e.id;
        this.motor.block();
        this.motor.z = Math.min(this.motor.z, e.z - CAR_HALF_LENGTH - 0.01);
        this.hit(type, e, "frontal");
        continue;
      }
      this.crash(e, `frontal ${vertical} y${this.motor.y.toFixed(2)} top${surfaceTop(e, car.z).toFixed(2)}`);
      if (this.ended) return;
    }
    this.touching = nowTouching;
  }

  /** A board or shield takes a crash or the catch: the road ahead cleared, a hop, 1 s of immunity (the original's auto-revive). */
  private applySave(saved: "board" | "shield"): void {
    this.world.clearAhead(this.motor.z, RV.cleanupDistance * this.speedModifier());
    this.stumble.revive();
    this.motor.forceJump(this.motorEvents);
    this.hooks.cue("crash");
    this.hooks.shake();
    this.hooks.toast(saved === "board" ? "BOARD BROKE · KEEP GOING" : "SHIELD TOOK THE HIT");
  }

  /** The top of a solid thing standing in `lane` at `z`, or null. */
  private solidTopAt(lane: number, z: number): number | null {
    let top: number | null = null;
    for (const e of this.world.entities) {
      if (!e.active || isCollectible(e.kind) || entityDefinitions[e.kind].trigger) continue;
      if (Math.abs(e.x - lane * W.laneWidth) >= e.width / 2 + 0.7 || z < e.z - 1 || z > e.z + e.depth + 1) continue;
      if (e.yBase > 3) continue;                                       // overhead (a bridge deck)
      top = Math.max(top ?? 0, e.yBase + e.height);
    }
    return top;
  }

  /** Rolling under a bar: the car cannot stand up until it is clear (it would rise into the bar). */
  private holdRollUnder(): void {
    if (this.motor.state !== "rolling") return;
    const full = M.colliderHeight, low = M.colliderHeight * M.rollHeightFactor;
    for (const e of this.world.entities) {
      if (!e.active || isCollectible(e.kind) || entityDefinitions[e.kind].trigger) continue;
      if (e.z > this.motor.z + CAR_HALF_LENGTH + 0.3 || e.z + e.depth < this.motor.z - CAR_HALF_LENGTH) continue;
      if (Math.abs(e.x - this.motor.x) >= e.width / 2 + M.colliderHalfWidth) continue;
      const above = e.yBase - this.motor.y;
      if (above >= low - 0.02 && above < full) { this.motor.holdRoll(); return; }
    }
  }

  /** Where along it a side contact is: the front or back 1.6–2.6 m (more at speed), or the middle. */
  private sideDepth(e: WorldEntity): "front" | "back" | "middle" {
    const frac = Math.min(1, Math.max(0, (this.motor.rampSpeed - M.startSpeed) / (M.endSpeed - M.startSpeed)));
    const d = Math.min(e.depth / 2, M.sideDepthMin + (M.sideDepthMax - M.sideDepthMin) * frac);
    const z = this.motor.z;
    if (z < e.z + d) return "front";
    if (z > e.z + e.depth - d) return "back";
    return "middle";
  }

  private collect(e: WorldEntity): void {
    e.active = false;
    switch (e.kind) {
      case "coin": {
        const n = this.powers.doubleCoinsLeft > 0 ? 2 : 1;
        this.coins += n;
        this.metrics.coins += n;
        if (this.powers.active.has("magnet")) this.metrics.coinsWithMagnet += n;
        if (this.motor.airborne) this.metrics.coinsInAir += n;
        if (this.powers.active.has("sneakers")) this.metrics.coinsWithSneakers += n;
        if (this.powers.active.has("jetpack")) this.metrics.coinsWithJetpack += n;
        this.scoreAcc += SC.pointsPerCoin * (SC.baseMultiplier + this.missionMultiplier) * n;   // the original: coins use the base multiplier only
        this.hooks.cue("coin");
        return;
      }
      case "key":
        this.keys += 1;
        this.metrics.keys += 1;
        this.hooks.cue("key");
        this.hooks.toast("+1 KEY");
        return;
      case "mysteryBox":
        this.metrics.boxes += 1;
        this.hooks.cue("box");
        this.hooks.toast("MYSTERY BOX");
        return;
      case "letter":
        if (e.letter) this.metrics.letters.push(e.letter);
        this.hooks.cue("key");
        this.hooks.toast(`LETTER ${e.letter ?? ""}`);
        return;
      case "token":
        this.metrics.tokens += 1;
        this.hooks.cue("key");
        return;
      case "pickup": {
        const power = e.power ?? "magnet";
        this.metrics.pickups[power] = (this.metrics.pickups[power] ?? 0) + 1;
        this.metrics.pickupsByLane[String(this.motor.lane)] = (this.metrics.pickupsByLane[String(this.motor.lane)] ?? 0) + 1;
        if (power === "magnet") this.metrics.magnets += 1;
        this.scoreAcc += SC.pointsPerPickup * this.multiplier;
        this.powers.activate(power, this.motor, this.powerEvents());
        if (power === "jetpack") this.beginFlight("jetpack");
        if (power === "sneakers") this.stumble.recover();          // the springs clear a running stumble (_recoverFromStumble)
        if (power === "pogo") this.world.spawnPogoCoins(this.motor.z, this.motor.speed, this.motor.y);
        return;
      }
    }
  }

  private powerEvents() {
    return {
      toast: (m: string) => this.hooks.toast(m),
      cue: (n: string) => this.hooks.cue(n as CueName),
      bonusMultiplier: (amount: number) => { this.mysteryBonus += amount; },
      grantBoard: () => { this.boards += 1; }
    };
  }

  /** The flight is over above its landing section (open road, nothing to clear): the car falls onto it. */
  private onFlightEnd(kind: FlightKind): void {
    this.flightLanding = null;
    if (kind === "headstart") {
      // the headstart lands you further up the speed ramp and drops magnet, 2X and springs ahead (shuffled over shuffled lanes)
      this.motor.elapsed += F.headstartLandSpeedOffset[this.headstartLevel - 1] * M.speedRampSeconds;
      const lanes = [-1, 0, 1].sort(() => this.rng() - 0.5);
      const items = [...F.headstartLandingItems].sort(() => this.rng() - 0.5);
      items.forEach((power, i) => {
        const z = this.motor.z + 40 + i * 15;
        const lane = [lanes[i], ...lanes.filter((l) => l !== lanes[i])].find((l) => this.solidTopAt(l, z) === null);
        if (lane !== undefined) this.world.add("pickup", lane, z, 1, { power });
        else this.world.add("pickup", lanes[i], z, (this.solidTopAt(lanes[i], z) ?? 0) + 1, { power });   // on the roof
      });
    }
  }

  /** A trip: the stumble window opens (the cop closes in); a second one inside it is the catch. No slow-down (the original has none). */
  private hit(type: StumbleType, e: WorldEntity | null, where: string): void {
    if (this.motor.state === "flying") return;
    // one trip per frame: pieces that touch (a ramp joining its truck) are one obstacle, not two trips at once
    if (this.trippedAt === this.frame) return;
    this.trippedAt = this.frame;
    const result = this.stumble.hit(type);
    if (result === "ignored") return;
    const kind = e?.kind ?? "wall";
    this.metrics.stumbles += 1;
    this.metrics.stumblesByKind[kind] = (this.metrics.stumblesByKind[kind] ?? 0) + 1;
    this.stumbleLog.push({ kind, where, z: Math.round(this.motor.z), carY: +this.motor.y.toFixed(2), state: this.motor.state, src: e?.src,
      carX: +this.motor.x.toFixed(2), lane: this.motor.lane, eLane: e?.lane, eZ: e ? +e.z.toFixed(1) : undefined, eTop: e ? +(e.yBase + e.height).toFixed(2) : undefined, vy: +this.motor.vy.toFixed(1) });
    if (this.stumbleLog.length > 12) this.stumbleLog.shift();
    this.hooks.shake();
    if (result === "death") { this.pendingDeath = true; this.hooks.cue("crash"); this.caught = true; return; }
    this.hooks.cue("stumble");
    this.hooks.cue("siren");
    const what = kind === "wall" ? "HIT THE WALL" : kind === "ethCoin" ? "ETH IN THE ROAD" : kind === "bush" ? "THROUGH THE HEDGE" : kind === "pole" ? "CLIPPED A POLE"
      : where === "corner" || where === "side" ? "CLIPPED IT" : "TRIPPED";
    this.hooks.toast(`${what} · ELON IS CLOSING IN`);
  }

  /** A frontal hit: a board or shield takes it, otherwise the run ends here. */
  private crash(e: WorldEntity, how = "frontal"): void {
    if (this.stumble.invincible || this.motor.state === "flying") return;
    this.lastCrash = { kind: e.kind, lane: e.lane, z: e.z, carY: this.motor.y, state: this.motor.state, how, src: e.src };
    const saved = this.powers.absorbCrash();
    if (saved) {
      e.active = false;
      e.cleared = true;
      this.applySave(saved);
      return;
    }
    this.finish(false);
  }

  private finish(caught: boolean): void {
    if (this.ended) return;
    this.ended = true;
    this.caught = caught;
    if (caught && this.metrics.caughtAtSeconds === null) this.metrics.caughtAtSeconds = this.elapsed;
    this.motor.kill();
    this.powers.endRun();
    this.hooks.cue("crash");
    this.hooks.shake();
    this.hooks.gameOver(caught);
  }

  private magnet(dt: number): void {
    const magnet = this.powers.active.has("magnet");
    const jumpMagnet = this.powers.jumpMagnet && this.motor.airborne;
    if (!magnet && !jumpMagnet) return;
    const reach = P.magnetRadius;
    const speed = (magnet ? P.magnetSpeed : P.sneakersMagnetSpeed) * this.speedModifier();   // Magnetizer: 150 u/s × speed ratio
    for (const e of this.world.entities) {
      if (!e.active || e.kind !== "coin") continue;
      const dz = e.z - this.motor.z;
      if (dz < -2 || dz > reach) continue;
      if (!magnet && Math.abs(e.x - this.motor.x) > 1) continue;
      // SOL on a truck roof is not pulled through the truck while the car is below the roof (the original tests a coin → car ray)
      if (this.motor.y < ROOF_HEIGHT - 1 && e.yBase > ROOF_HEIGHT - 0.5 && this.solidTopAt(Math.round(e.x / W.laneWidth), e.z) !== null) continue;
      const step = speed * dt;
      e.x += Math.sign(this.motor.x - e.x) * Math.min(Math.abs(this.motor.x - e.x), step);
      e.yBase += Math.sign(this.motor.y + 0.6 - e.yBase) * Math.min(Math.abs(this.motor.y + 0.6 - e.yBase), step);
      if (dz > 1) e.z -= Math.min(dz - 1, step * 0.8);
    }
  }
}
